// src/pages/bindings.tsx
import {
  ResourceContext,
  matchResourceFromRoute
} from "@refinedev/core";
import { useRouter } from "next/router";
import Link from "next/link";
import { parse, stringify } from "qs";
import React, { useContext } from "react";

// src/common/params-from-current-path/index.ts
var paramsFromCurrentPath = (pathname, matchingRoute) => {
  const params = {};
  const sanitizedMatchingRoute = matchingRoute.replace(/^\/|\/$/g, "");
  const sanitizedPathname = pathname.replace(/^\/|\/$/g, "");
  const matchingRouteParts = sanitizedMatchingRoute.split("/");
  const pathnameParts = sanitizedPathname.split("/");
  matchingRouteParts.forEach((part, index) => {
    var _a;
    if (part.startsWith(":")) {
      if (((_a = pathnameParts[index]) == null ? void 0 : _a.length) > 0) {
        params[part.replace(":", "")] = pathnameParts[index];
      }
    }
  });
  return params;
};

// src/common/convert-to-number-if-possible/index.ts
var convertToNumberIfPossible = (value) => {
  if (typeof value === "undefined") {
    return value;
  }
  const num = Number(value);
  if (`${num}` === value) {
    return num;
  }
  return value;
};

// src/pages/bindings.tsx
var stringifyConfig = {
  addQueryPrefix: true,
  skipNulls: true,
  arrayFormat: "indices",
  encode: false,
  encodeValuesOnly: true
};
var routerBindings = {
  go: () => {
    const { push, replace, asPath: pathname } = useRouter();
    const fn = React.useCallback(
      ({
        to,
        type,
        query,
        options: { keepQuery, keepHash } = {},
        hash
      }) => {
        let urlHash = "";
        if (keepHash && typeof document !== "undefined") {
          urlHash = document.location.hash;
        }
        if (hash) {
          urlHash = `#${hash.replace(/^#/, "")}`;
        }
        const urlQuery = {
          ...keepQuery ? parse(pathname.split("?")[1], {
            ignoreQueryPrefix: true
          }) : {},
          ...query
        };
        if (urlQuery.to) {
          urlQuery.to = encodeURIComponent(`${urlQuery.to}`);
        }
        const cleanPathname = pathname.split("?")[0].split("#")[0];
        const urlTo = to || cleanPathname;
        const hasUrlHash = urlHash.length > 1;
        const hasUrlQuery = Object.keys(urlQuery).length > 0;
        const fullPath = `${urlTo}${hasUrlQuery ? stringify(urlQuery, stringifyConfig) : ""}${hasUrlHash ? urlHash : ""}`;
        if (type === "path") {
          return fullPath;
        }
        if (type === "replace") {
          replace(fullPath, void 0, {
            shallow: typeof to === "undefined"
          });
        } else {
          push(fullPath);
        }
        return void 0;
      },
      [pathname, push, replace]
    );
    return fn;
  },
  back: () => {
    const { back } = useRouter();
    return back;
  },
  parse: () => {
    const { query, asPath: pathname, isReady } = useRouter();
    const { resources } = useContext(ResourceContext);
    const cleanPathname = pathname.split("?")[0].split("#")[0];
    const { resource, action, matchedRoute } = React.useMemo(() => {
      return matchResourceFromRoute(cleanPathname, resources);
    }, [cleanPathname, resources]);
    const inferredParams = matchedRoute && cleanPathname && isReady ? paramsFromCurrentPath(cleanPathname, matchedRoute) : {};
    const inferredId = inferredParams.id;
    const parsedParams = React.useMemo(() => {
      const searchParams = pathname.split("?")[1];
      return parse(searchParams, { ignoreQueryPrefix: true });
    }, [pathname]);
    const fn = React.useCallback(() => {
      const combinedParams = {
        ...inferredParams,
        ...query,
        ...parsedParams
      };
      const response = {
        ...resource && { resource },
        ...action && { action },
        ...inferredId && { id: decodeURIComponent(inferredId) },
        ...(query == null ? void 0 : query.id) && { id: decodeURIComponent(`${query == null ? void 0 : query.id}`) },
        pathname: cleanPathname,
        params: {
          ...combinedParams,
          current: convertToNumberIfPossible(
            combinedParams.current
          ),
          pageSize: convertToNumberIfPossible(
            combinedParams.pageSize
          ),
          to: combinedParams.to ? decodeURIComponent(combinedParams.to) : void 0
        }
      };
      return response;
    }, [
      pathname,
      query,
      resource,
      action,
      inferredParams,
      inferredId,
      parsedParams
    ]);
    return fn;
  },
  Link: React.forwardRef(function RefineLink({ to, ...props }, ref) {
    return /* @__PURE__ */ React.createElement(Link, {
      href: to,
      ...props,
      ref
    });
  })
};

// src/pages/refine-routes.tsx
import React2 from "react";
import { useResource } from "@refinedev/core";
import { useRouter as useRouter2 } from "next/router";
var RefineRoutes = ({ children }) => {
  const { resource, action } = useResource();
  const { asPath: pathname, isReady } = useRouter2();
  if (!isReady) {
    return /* @__PURE__ */ React2.createElement(React2.Fragment, null);
  }
  const resourceAction = resource && action ? resource[action] : void 0;
  const ResourceActionComponent = typeof resourceAction === "function" ? resourceAction : typeof resourceAction === "object" ? resourceAction.component : void 0;
  return /* @__PURE__ */ React2.createElement(React2.Fragment, null, children ? children(
    ResourceActionComponent ? /* @__PURE__ */ React2.createElement(ResourceActionComponent, null) : void 0,
    pathname
  ) : ResourceActionComponent ? /* @__PURE__ */ React2.createElement(ResourceActionComponent, null) : void 0);
};

// src/pages/navigate-to-resource.tsx
import { useResource as useResource2, useGetToPath } from "@refinedev/core";
import React3 from "react";
import { useRouter as useRouter3 } from "next/router";
var NavigateToResource = ({
  resource: resourceProp,
  meta
}) => {
  const ran = React3.useRef(false);
  const { replace } = useRouter3();
  const getToPath = useGetToPath();
  const { resource, resources } = useResource2(resourceProp);
  const toResource = React3.useMemo(
    () => resource || resources.find((r) => r.list),
    [resource, resources]
  );
  React3.useEffect(() => {
    if (toResource) {
      if (!ran.current) {
        const path = getToPath({
          resource: toResource,
          action: "list",
          meta
        });
        if (path) {
          replace(path);
        }
        ran.current = true;
      }
    }
  }, [toResource, replace, meta, getToPath]);
  return null;
};

// src/pages/unsaved-changes-notifier.tsx
import React4 from "react";
import { useTranslate, useWarnAboutChange } from "@refinedev/core";
import { useRouter as useRouter4 } from "next/router";
var UnsavedChangesNotifier = ({
  translationKey = "warnWhenUnsavedChanges",
  message = "Are you sure you want to leave? You have unsaved changes."
}) => {
  const translate = useTranslate();
  const router = useRouter4();
  const { warnWhen, setWarnWhen } = useWarnAboutChange();
  const warnMessage = React4.useMemo(() => {
    return translate(translationKey, message);
  }, [translationKey, message, translate]);
  const warnWhenListener = React4.useCallback(
    (e) => {
      e.preventDefault();
      e.returnValue = warnMessage;
      return e.returnValue;
    },
    [warnMessage]
  );
  React4.useEffect(() => {
    if (warnWhen) {
      window.addEventListener("beforeunload", warnWhenListener);
    }
    return () => window.removeEventListener("beforeunload", warnWhenListener);
  }, [warnWhen, warnWhenListener]);
  React4.useEffect(() => {
    const routeChangeStart = () => {
      if (warnWhen) {
        const allowTransition = window.confirm(warnMessage);
        if (allowTransition) {
          setWarnWhen == null ? void 0 : setWarnWhen(false);
        } else {
          router.events.emit("routeChangeError");
          throw "Abort route change due to unsaved changes prompt. Ignore this error.";
        }
      }
    };
    router.events.on("routeChangeStart", routeChangeStart);
    return () => router.events.off("routeChangeStart", routeChangeStart);
  }, [warnWhen, warnMessage, setWarnWhen]);
  return null;
};

// src/common/parse-table-params.ts
import { parse as parse2 } from "qs";
var parseTableParams = (search) => {
  const parsed = parse2(search, { ignoreQueryPrefix: true });
  const tableReady = {
    ...parsed,
    pagination: {
      current: parsed.current,
      pageSize: parsed.pageSize
    }
  };
  delete tableReady.current;
  delete tableReady.pageSize;
  return tableReady;
};
export {
  NavigateToResource,
  RefineRoutes,
  UnsavedChangesNotifier,
  routerBindings as default,
  paramsFromCurrentPath,
  parseTableParams,
  stringifyConfig
};
//# sourceMappingURL=index.js.map